/**
 * Echo Self engine — solo-mode comparison against your past self.
 *
 * When a user plays solo, we compare their CURRENT ranking to their PAST
 * ranking of the same content (same category + same 5 elements + same prompt).
 * The match percentage uses the same Kendall-tau-derived metric as the bonded
 * twin rate (`computeTwinRate` in game/sync.ts). This replaces the previous
 * "twin rate vs randomized self" path that fabricated meaningless numbers.
 *
 * Storage is local-only: echoes never leave the device.
 */

import type { Pulse } from "$data/types";

const STORAGE_PREFIX = "vutwin.echo.";
const PRUNE_AFTER_DAYS = 30;

/** Persisted shape under each `vutwin.echo.<sig>` key. */
interface EchoEntry {
  /** The user's most recent ordering for this content (length 5). */
  order: string[];
  /** Unix ms timestamp of the last save — used by pruneEchoes. */
  ts: number;
}

/**
 * Stable per-content signature.
 *
 * Two pulses with the same category, same 5 element ids (in any order),
 * and the same prompt text resolve to the same signature so a replay of
 * "today's Cities" lines up with the morning's saved order.
 *
 * djb2 → unsigned 32-bit, hex-padded — short, collision-resistant enough
 * for a single-device localStorage namespace, and zero crypto dependencies
 * (so it works in both client and SSR contexts without polyfills).
 */
export function pulseSignature(pulse: Pulse): string {
  const elements = [...pulse.elements.map((e) => e.id)].sort().join(",");
  const input = `${pulse.categoryId}|${elements}|${pulse.prompt.text}`;
  let h = 5381 >>> 0;
  for (let i = 0; i < input.length; i++) {
    h = (((h << 5) + h) ^ input.charCodeAt(i)) >>> 0;
  }
  return h.toString(16).padStart(8, "0");
}

/** Best-effort localStorage shim — SSR / private-mode safe. */
function readStorage(): Storage | null {
  try {
    if (typeof localStorage === "undefined") return null;
    return localStorage;
  } catch {
    return null;
  }
}

/** Load the previous order for `sig`, or null if none / corrupt. */
export function loadEcho(sig: string): string[] | null {
  const ls = readStorage();
  if (!ls) return null;
  const raw = ls.getItem(STORAGE_PREFIX + sig);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as EchoEntry;
    if (
      parsed &&
      Array.isArray(parsed.order) &&
      parsed.order.length === 5 &&
      parsed.order.every((x) => typeof x === "string")
    ) {
      return parsed.order;
    }
  } catch {
    /* corrupt entry — fall through to drop it */
  }
  ls.removeItem(STORAGE_PREFIX + sig);
  return null;
}

/** Persist this play's ordering as the new echo baseline for `sig`. */
export function saveEcho(sig: string, order: string[]): void {
  if (order.length !== 5) return;
  const ls = readStorage();
  if (!ls) return;
  const entry: EchoEntry = { order: [...order], ts: Date.now() };
  try {
    ls.setItem(STORAGE_PREFIX + sig, JSON.stringify(entry));
  } catch {
    /* quota exceeded — silently drop; pruneEchoes will catch up next call */
  }
}

/**
 * Drop echo entries older than `maxDays`. Bounded localStorage growth
 * for users who play across many categories over many weeks.
 */
export function pruneEchoes(maxDays = PRUNE_AFTER_DAYS): void {
  const ls = readStorage();
  if (!ls) return;
  const cutoff = Date.now() - maxDays * 24 * 60 * 60 * 1000;
  const toRemove: string[] = [];
  for (let i = 0; i < ls.length; i++) {
    const k = ls.key(i);
    if (!k || !k.startsWith(STORAGE_PREFIX)) continue;
    try {
      const raw = ls.getItem(k);
      if (!raw) continue;
      const parsed = JSON.parse(raw) as EchoEntry;
      if (!parsed || typeof parsed.ts !== "number" || parsed.ts < cutoff) {
        toRemove.push(k);
      }
    } catch {
      toRemove.push(k);
    }
  }
  for (const k of toRemove) ls.removeItem(k);
}

/** Public shape returned by `listEchoes`. */
export interface EchoRecord {
  /** localStorage key suffix — stable per-content signature. */
  sig: string;
  /** Unix ms when this echo was last saved. */
  ts: number;
  /** The user's last 5-element ranking for this content. */
  order: string[];
}

/**
 * Enumerate every echo entry currently in localStorage.
 *
 * Returns one record per content signature, sorted newest-first. Used by
 * the Settings "Past plays" section. Skips and prunes corrupt entries
 * inline so a malformed value in storage doesn't break the listing.
 */
export function listEchoes(): EchoRecord[] {
  const ls = readStorage();
  if (!ls) return [];
  const out: EchoRecord[] = [];
  const drop: string[] = [];
  for (let i = 0; i < ls.length; i++) {
    const k = ls.key(i);
    if (!k || !k.startsWith(STORAGE_PREFIX)) continue;
    const sig = k.slice(STORAGE_PREFIX.length);
    const raw = ls.getItem(k);
    if (!raw) {
      drop.push(k);
      continue;
    }
    try {
      const parsed = JSON.parse(raw) as EchoEntry;
      if (
        !parsed ||
        !Array.isArray(parsed.order) ||
        parsed.order.length !== 5 ||
        !parsed.order.every((x) => typeof x === "string") ||
        typeof parsed.ts !== "number"
      ) {
        drop.push(k);
        continue;
      }
      out.push({ sig, ts: parsed.ts, order: [...parsed.order] });
    } catch {
      drop.push(k);
    }
  }
  for (const k of drop) ls.removeItem(k);
  out.sort((a, b) => b.ts - a.ts);
  return out;
}

/** Drop a single echo by signature. No-op if missing. */
export function deleteEcho(sig: string): void {
  const ls = readStorage();
  if (!ls) return;
  ls.removeItem(STORAGE_PREFIX + sig);
}

/** Wipe every echo entry. Used by Settings "Clear past plays" / "Reset". */
export function clearAllEchoes(): void {
  const ls = readStorage();
  if (!ls) return;
  const drop: string[] = [];
  for (let i = 0; i < ls.length; i++) {
    const k = ls.key(i);
    if (k && k.startsWith(STORAGE_PREFIX)) drop.push(k);
  }
  for (const k of drop) ls.removeItem(k);
}

/** Tier label band for an echo percentage. */
export function echoTier(pct: number): string {
  if (pct >= 100) return "LOCKED IN · Mirror";
  if (pct >= 80) return "CONSISTENT · Steady";
  if (pct >= 50) return "EVOLVING · Drifting";
  return "REWRITING · New You";
}
